// frontend/src/pages/TermsOfService.js
import React from 'react';
import { Box, Container, Typography, Link, List, ListItem } from '@mui/material';
import { Helmet } from 'react-helmet-async';

const H5 = (p) => <Typography variant="h5" fontWeight={700} gutterBottom {...p} />;
const P = (p) => <Typography sx={{ mb: 2 }} {...p} />;

export default function TermsOfService() {
  const updated = 'January 3, 2025';

  return (
    <>
      <Helmet>
        <title>Terms of Service - Blackmount</title>
        <meta name="description" content="The legal terms and conditions governing use of Blackmount.ai services, applications, and platform." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.blackmount.ai/terms" />
      </Helmet>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h3" fontWeight={800} gutterBottom>Terms of Service</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated {updated}
        </Typography>

        <P>
          Welcome to <strong>Blackmount.ai Inc.</strong> (“<strong>Blackmount</strong>,” “we,” “us,” or “our”).
          These Terms of Service (“<strong>Terms</strong>”) govern your access to and use of our websites
          (<Link href="https://www.blackmount.ai" target="_blank" rel="noopener">blackmount.ai</Link>), web app
          (<Link href="https://app.blackmount.ai" target="_blank" rel="noopener">app.blackmount.ai</Link>), browser extension,
          and mobile/desktop apps (collectively, the “<strong>Services</strong>”). By using the Services, you agree to these Terms.
          If you do not agree, please do not use the Services.
        </P>

        <H5>1) Eligibility & Accounts</H5>
        <P>
          You must be at least <strong>13 years old</strong> to use the Services, except if you are located in the European Union (EU),
          where you must be at least <strong>16 years old</strong>. If you are under 18, you represent that you have parental/guardian
          consent. You are responsible for maintaining the security of your account credentials and for all activity under your account.
        </P>

        <H5>2) Acceptable Use</H5>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>Do not misuse the Services or attempt to access them by unauthorized means.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Do not upload unlawful, harmful, or infringing content, or violate others’ rights.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            Do not <strong>reverse engineer, decompile, disassemble, or benchmark</strong> the Services or their models/algorithms without our prior written consent.
          </ListItem>
          <ListItem sx={{ display: 'list-item' }}>
            Comply with recording/consent laws: if you record voice or meetings, you must obtain all legally required consents.
          </ListItem>
        </List>

        <H5>3) User Content</H5>
        <P>
          You retain ownership of the content you submit (e.g., bookmarks, files, voice notes, transcripts, projects). By submitting content,
          you grant Blackmount a limited, worldwide, non-exclusive license to host, process, and display it solely to provide the Services to you
          and (if applicable) your organization. You represent that you have the necessary rights to the content you upload.
        </P>
        <P>
          <strong>You are solely responsible for the content you submit.</strong> Blackmount does not endorse, monitor, or assume liability for content uploaded by users.
          You must ensure your content complies with applicable laws, including copyright and privacy regulations.
        </P>

        <H5>4) Intellectual Property</H5>
        <P>
          The Services—including software, documentation, designs, and trademarks (such as <strong>Blackmount™</strong>)—are owned by Blackmount or our licensors
          and are protected by law. No rights are granted except as necessary for you to use the Services under these Terms.
        </P>

        <H5>5) Payments & Subscriptions</H5>
        <P>
          We offer both free and paid subscription tiers. Paid features include premium storage, extended voice recording, and enhanced AI capabilities.
          Payments are processed by trusted third-party providers (e.g., Stripe). For iOS purchases, payments are processed through Apple's App Store and Apple's terms apply; manage subscriptions in iOS Settings &gt; Apple ID &gt; Subscriptions. <strong>All fees are non-refundable</strong> except where required by law.
          We will notify you in advance of pricing changes.
        </P>
        <P>
          <strong>Subscription Cancellation Policy:</strong>
        </P>
        <List dense sx={{ listStyle: 'disc', pl: 3, mb: 2 }}>
          <ListItem sx={{ display: 'list-item' }}>You may cancel your subscription at any time through <Link href="/settings">Settings</Link> or the Stripe billing portal.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Cancellations take effect at the end of your current billing period (monthly or annual).</ListItem>
          <ListItem sx={{ display: 'list-item' }}><strong>No refunds or credits will be issued for partial billing periods.</strong></ListItem>
          <ListItem sx={{ display: 'list-item' }}>You retain access to paid features until the end of your paid period.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Canceled subscriptions will not auto-renew. You may resubscribe at any time.</ListItem>
          <ListItem sx={{ display: 'list-item' }}>Downgrading from a higher tier may result in loss of access to premium features at the end of the billing period.</ListItem>
        </List>
        <P>
          <strong>Freemium Model:</strong> Some Services may currently be offered free of charge. We reserve the right to modify, limit, or discontinue the freemium model at any time.
          This may include introducing usage limits, requiring payment for continued access, or discontinuing free access altogether.
          If we make such changes, we will provide advance notice where required by law.
        </P>

        <H5>6) Termination</H5>
        <P>
          We may suspend or terminate your access to the Services if you violate these Terms, misuse the Services, or as required by law.
          You may stop using the Services at any time. Upon termination, your right to use the Services ends, but provisions that by their nature should survive
          (e.g., intellectual property, disclaimers, and limitations of liability) will remain in effect.
        </P>
        <P>
          We reserve the right to delete inactive accounts and associated data after 12 months of inactivity, as further described in our <Link href="/privacy-policy">Privacy Policy</Link>.
        </P>

        <H5>7) Disclaimer of Warranties</H5>
        <P>
          The Services are provided "<strong>as is</strong>" and "<strong>as available</strong>," without warranties of any kind, express or implied, including merchantability,
          fitness for a particular purpose, and non-infringement. Outputs generated by AI (e.g., summaries, tags, recommendations) are <strong>informational only</strong>
          and may contain errors. <strong>You should not rely on AI outputs for legal, medical, or financial advice.</strong>
        </P>
        <P>
          Some features may be labeled as <strong>Beta</strong>, <strong>Preview</strong>, or <strong>Experimental</strong>. Such features are provided "as is,"
          may contain errors, and may be modified, limited, or discontinued at any time without liability to you.
        </P>

        <H5>8) Limitation of Liability</H5>
        <P>
          To the maximum extent permitted by law, Blackmount and its affiliates will not be liable for indirect, incidental, special, consequential,
          exemplary, or punitive damages, or any loss of data, profits, or business opportunities, arising out of or related to your use of the Services,
          even if advised of the possibility of such damages.
        </P>
        <P>
          <strong>Indemnification:</strong> To the extent permitted by law, you agree to indemnify and hold harmless Blackmount, its affiliates, and employees from any claims, damages,
          or expenses arising from your misuse of the Services or violation of these Terms, including but not limited to unlawful recording of voice or meetings.
        </P>

        <H5>9) International Users</H5>
        <P>
          If you access the Services from outside the United States, you consent to processing in the United States and other countries where our providers operate.
          For EU/UK users, international transfers are subject to safeguards such as Standard Contractual Clauses (SCCs), as further described in our
          <Link href="/privacy" sx={{ ml: 0.5 }}>Privacy Policy</Link>.
        </P>

        <H5>10) Changes to Terms</H5>
        <P>
          We may update these Terms from time to time. For material changes, we will provide notice (e.g., email or in-app) and, where required, obtain consent.
          Your continued use of the Services after the effective date of the updated Terms constitutes acceptance.
        </P>

        <H5>11) Governing Law & Venue</H5>
        <P>
          These Terms are governed by the laws of the State of <strong>Delaware</strong>, United States, without regard to conflict of law principles.
          You agree that the exclusive venue for any disputes will be the state or federal courts located in <strong>California or Delaware</strong>, unless otherwise required by law.
        </P>

        <H5>12) Export Control & Prohibited Uses</H5>
        <P>
          You may not use the Services in violation of U.S. export control laws or sanctions. The Services are not intended for use in embargoed countries
          or by restricted parties. You represent that you are not located in, or a national of, any country subject to U.S. government embargo or designated
          as a "terrorist supporting" country, and that you are not on any U.S. government list of prohibited or restricted parties.
        </P>

        <H5>13) Contact</H5>
        <P>
          Questions about these Terms? Contact us:<br/>
          Email: <Link href="mailto:support@blackmount.ai">support@blackmount.ai</Link><br/>
          Mail: Blackmount.ai Inc., PO Box 641, Palo Alto, CA 94301, United States
        </P>

        <Box sx={{ mt: 4, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            This document is provided for convenience and does not replace legal advice. For enterprise agreements,
            please contact us for a separate contract.
          </Typography>
        </Box>
      </Container>
    </>
  );
}

